import { WebhookEndpointStatus } from '@compass/client/types/webhook';
import { ArrowRight, Webhook } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useShouldShowWebhooksPage } from '@/hooks/useShouldShowWebhooksPage';
import { useWebhookEndpoints } from '@/pages/webhooks/hooks/useWebhookEndpoints';
import { webhooksPath } from '@/pages/webhooks/utils';

/** "2 endpoints · 1 paused" — the count, plus how many are not delivering. */
function endpointSummary(statuses: (WebhookEndpointStatus | undefined)[]): string {
  if (statuses.length === 0) return 'No endpoints yet';
  const count = (status: WebhookEndpointStatus) => statuses.filter(s => s === status).length;
  const parts = [`${statuses.length} ${statuses.length === 1 ? 'endpoint' : 'endpoints'}`];
  const paused = count(WebhookEndpointStatus.WEBHOOK_ENDPOINT_STATUS_PAUSED);
  const disabled = count(WebhookEndpointStatus.WEBHOOK_ENDPOINT_STATUS_DISABLED);
  if (paused > 0) parts.push(`${paused} paused`);
  if (disabled > 0) parts.push(`${disabled} disabled`);
  return parts.join(' · ');
}

/**
 * Settings → Developer entry point to the Webhooks pages (endpoints and their
 * deliveries), which need a full-width page of their own. Shown under the same
 * gate as those pages: the per-org flag `webhooks_<org_id>` plus the
 * ListWebhookEndpoints permission (Organization Admin).
 */
export function WebhooksSection() {
  const { shouldShow, organizationId } = useShouldShowWebhooksPage();
  const endpointsQuery = useWebhookEndpoints(organizationId ?? '', shouldShow);

  if (!shouldShow || !organizationId) {
    return null;
  }

  const endpoints = endpointsQuery.data?.endpoints ?? [];

  return (
    <div className="mt-6 rounded-lg border bg-card p-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted">
            <Webhook className="h-6 w-6 text-foreground" />
          </div>
          <div>
            <h3 className="text-sm font-semibold">Webhooks</h3>
            <p className="text-sm text-muted-foreground">
              Receive signed case events at your own endpoints, and watch and retry their
              deliveries.
            </p>
            <div className="mt-1 text-xs text-muted-foreground">
              {endpointsQuery.isLoading ? (
                <Skeleton className="h-4 w-32" />
              ) : endpointsQuery.isError ? (
                'Could not load endpoints'
              ) : (
                endpointSummary(endpoints.map(e => e.status))
              )}
            </div>
          </div>
        </div>
        <Button variant="outline" className="shrink-0" asChild>
          <Link to={webhooksPath(organizationId)} data-analytics-id="settings:developer:webhooks">
            Manage webhooks
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}
